import { cn } from "@/lib/utils";
import * as React from "react";

/**
 * PageHeader — matches the reference design:
 * - Optional breadcrumb trail (uppercase mono, slash-separated)
 * - Large title (bold, near-black)
 * - Optional meta line (date, counts, etc.)
 * - Optional right-side actions slot
 */

function PageHeader({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="page-header"
      className={cn("space-y-1", className)}
      {...props}
    >
      {children}
    </div>
  );
}

function PageHeaderBreadcrumb({
  className,
  items,
  ...props
}: React.ComponentProps<"div"> & {
  items: Array<{ label: string; href?: string }>;
}) {
  return (
    <div
      data-slot="page-header-breadcrumb"
      className={cn("flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-mono", className)}
      {...props}
    >
      {items.map((item, index) => (
        <React.Fragment key={item.label}>
          {index > 0 ? <span className="text-border">/</span> : null}
          {item.href ? (
            <a
              href={item.href}
              className="hover:text-foreground transition-colors"
            >
              {item.label}
            </a>
          ) : (
            <span>{item.label}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function PageHeaderTitle({
  className,
  ...props
}: React.ComponentProps<"h1">) {
  return (
    <h1
      data-slot="page-header-title"
      className={cn("text-2xl font-bold tracking-tight text-foreground", className)}
      {...props}
    />
  );
}

function PageHeaderMeta({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="page-header-meta"
      className={cn("flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

function PageHeaderActions({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="page-header-actions"
      className={cn("flex flex-wrap items-center gap-2", className)}
      {...props}
    />
  );
}

function PageHeaderRow({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="page-header-row"
      className={cn("flex flex-wrap items-start justify-between gap-4", className)}
      {...props}
    />
  );
}

export {
  PageHeader,
  PageHeaderBreadcrumb,
  PageHeaderTitle,
  PageHeaderMeta,
  PageHeaderActions,
  PageHeaderRow,
};
